import { Input, type InputProps } from "@/components/ui/input";
import { useEffect, useState } from "react";

export interface DebouncedInputProps extends Omit<InputProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

/**
 * Un Input que maneja su propio estado local mientras escribes,
 * y solo notifica al padre (mediante onChange) después del tiempo especificado.
 * Esto evita re-renderizados costosos del componente padre en cada tecla.
 */
export function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 800,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);

  // Sincronizar el valor si cambia desde afuera
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // Si el valor local coincide con el inicial, no notificamos de nuevo
    if (value === initialValue) {
      return;
    }

    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange, initialValue]);

  return <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} />;
}
